import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import { getClonalityStatusSummary } from './clonalityStatusSummaryUtils';

function StatusMetric({ label, value, tooltip }) {
  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 0.5,
          border: '1px solid #d8e5ef',
          borderRadius: 1,
          px: 1,
          py: 0.5,
          backgroundColor: '#f8fbfd',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: '#20558a', fontWeight: 700 }}>
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function ClonalityAnalysisDialog({
  open,
  onClose,
  children = null,
  executions = [],
  analysisStatus = null,
}) {
  const statusSummary = getClonalityStatusSummary({
    executions,
    awaitingSampleCount: analysisStatus?.awaitingClonalitySampleCount,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: 'calc(100vw - 32px)',
          maxWidth: 'calc(100vw - 32px)',
          m: 2
        }
      }}
    >
      <DialogTitle sx={{ pr: 6, pb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="span">
              Clonality Analysis
            </Typography>
            <Chip label="Beta" size="small" color="info" />
          </Box>
          <Box
            role="status"
            aria-label="Clonality analysis status"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <StatusMetric
              label="Last run"
              value={statusSummary.lastRunLabel}
              tooltip="Most recent completed clonality analysis run."
            />
            <StatusMetric
              label="Next run"
              value={statusSummary.nextRunLabel}
              tooltip="Estimated from the latest completed run plus six hours."
            />
            <StatusMetric
              label="To analyze"
              value={statusSummary.awaitingSampleCountLabel}
              tooltip="Samples expected in the next clonality analysis run."
            />
          </Box>
        </Box>
        <IconButton
          aria-label="close-clonality-analysis"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: '20px' }}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

ClonalityAnalysisDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  executions: PropTypes.arrayOf(PropTypes.shape({
    attributes: PropTypes.shape({
      completed_at: PropTypes.string,
      created_at: PropTypes.string,
      status: PropTypes.string,
    }),
  })),
  analysisStatus: PropTypes.shape({
    awaitingClonalitySampleCount: PropTypes.number,
  }),
};

export default ClonalityAnalysisDialog;
